import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import DataTable from "@/components/layout/DataTable.vue";
import Card from "@/components/layout/Card.vue";
import {useI18n} from "vue3-i18n";
import {Member} from "@/types/entities/member";
import {computed, ref} from "vue";
import {useMemberStore} from "@/stores/memberStore";
import {Guid, Role} from "@/types";
import {useMemberService, useUserService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";
import {getAuthenticatedMemberOrganizationRoute} from "@/router/helpers";

// eslint-disable-next-line

export default /*#__PURE__*/_defineComponent({
  __name: 'MemberTable',
  props: {
    members: {},
    organizationId: {},
    cardLinkPath: {},
    isLoading: { type: Boolean },
    hideCardLink: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

const {t} = useI18n()
const memberStore = useMemberStore()
const userService = useUserService()
const memberService = useMemberService()

const members = ref<Member[]>(props.members);

const canAddEditOrDeleteMember = memberStore.hasOneOfTheseRoles([Role.Admin, Role.RegularSyndicateAdmin, Role.RegularSyndicateManager])
const cardLinkPath = canAddEditOrDeleteMember && !props.hideCardLink ? props.cardLinkPath : undefined
const cardLinkText = canAddEditOrDeleteMember && !props.hideCardLink ? t('organization.addMember') : undefined

const tableMembers = computed(() => {
  return members.value.map((member: any) => {
    let item = {...member}
    item.roles = member.roles ? member.roles.map((x: any) => t(`user.roles.${x}`)).join(', ') : undefined
    if (canAddEditOrDeleteMember) {
      item.actions = {
        resendUserInvite: true,
        update: {
          name: memberStore.hasRole(Role.Admin)
              ? 'admin.children.organizations.editMember'
              : `${getAuthenticatedMemberOrganizationRoute()}.children.editMember`,
          params: {organizationId: member.organizationId, memberId: member.id}
        },
        delete: member.email != memberStore.getMember.email
      }
    }
    return item;
  })
})

async function resendUserInvite(userId: string) {
  let response = await userService.resendInvite(userId)

  if (response.succeeded) {
    notifySuccess(t('user.inviteSuccessfullySent'))
  } else {
    let errorMessages = response.getErrorMessages('user', 'user.sendingInviteFailed')
    notifyError(errorMessages[0])
  }
}

async function deleteMember(member: any) {
  if (member?.id == null || !Guid.isValid(member.id ?? ""))
    return

  let confirmDelete = confirm(t("validation.member.delete.confirmation"));
  if (!confirmDelete)
    return;

  let response = await memberService.deleteMember(member.id, props.organizationId)
  if (response.succeeded) {
    members.value = members.value.filter(x => x.id !== member.id)
    notifySuccess(t('validation.member.delete.success'))
  } else {
    let errorMessages = response.getErrorMessages('validation.member.delete',
        'validation.member.delete.errorOccurred')
    notifyError(errorMessages[0])
  }
}

let memberHeader = [
  {
    text: t("global.firstName"),
    value: "firstName",
    sortable: true,
    width: 125,
  },
  {
    text: t("global.lastName"),
    value: "lastName",
    sortable: true,
    width: 125,
  },
  {
    text: t("global.email"),
    value: "email",
    sortable: true,
    width: 100,
  },
  {
    text: t("global.roles"),
    value: "roles",
    sortable: true,
    width: 200,
  }
]

if (canAddEditOrDeleteMember) {
  memberHeader.push({
    text: t("global.table.actions"),
    value: "actions",
    sortable: false,
    width: 125
  })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Card, {
    "link-path": _unref(cardLinkPath),
    "link-text": _unref(cardLinkText),
    title: tableMembers.value.length > 1 ? _unref(t)('global.members') : _unref(t)('global.member')
  }, {
    default: _withCtx(() => [
      _createVNode(DataTable, {
        headers: _unref(memberHeader),
        "is-loading": _ctx.isLoading,
        items: tableMembers.value,
        onDelete: deleteMember,
        onResendUserInvite: resendUserInvite
      }, null, 8, ["headers", "is-loading", "items"])
    ]),
    _: 1
  }, 8, ["link-path", "link-text", "title"]))
}
}

})